/** @format */

import {useState} from 'react';

import {Check, Close} from '@mui/icons-material';

import {format} from 'date-fns';
import {ru} from 'date-fns/locale';

import {
  CancelDeleteButton,
  DeleteNoteButton,
  Note,
  NoteBody,
  NoteBodyWrapper,
  NoteContent,
  NoteHeader,
  OnDeleteOverlay,
  OverlayContent,
  IconButton,
} from './styles';
import BinIcon from './icons/bin-icon.svg?react';

type Props = {
  note?: {id: string; text: string; insertedAt: string};
  isNew?: boolean;
  companyId: string;
  noteDate: string;
  onCreate?: (opts: {companyId: string; noteDate: string; text: string}) => Promise<any>;
  onUpdate?: (opts: {id: string; text: string}) => Promise<any>;
  onDelete: (opts: {id: string; isNew?: boolean}) => Promise<any>;
};

const NoteTextField = ({
  note,
  isNew = false,
  companyId,
  noteDate,
  onCreate,
  onUpdate,
  onDelete,
}: Props) => {
  const {id, text, insertedAt} = note || {};
  const [value, setValue] = useState(text || '');
  const [isDisabled, setIsDisabled] = useState(false);
  const [initial, setInitial] = useState(text || '');
  const dateDisplay = format(insertedAt || new Date(), 'd MMMM yyyy', {
    locale: ru,
  });

  const isEmpty = value.trim() === '';
  const isDirty = isNew ? !isEmpty : value.trim() !== (initial || '').trim();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    setValue(text);
  };

  const handleDelete = async () => {
    await onDelete({id: id || '', isNew});
    setIsDisabled(false);
  };

  const submitNote = async () => {
    if (isEmpty) return;

    if (isNew && onCreate) {
      await onCreate({companyId, noteDate, text: value});
    } else if (id && onUpdate) {
      await onUpdate({id, text: value});
      setInitial(value);
    }
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      await submitNote();
    }
  };

  const handleDeleteConfirm = async () => {
    if (isNew) {
      await handleDelete();
    } else {
      setIsDisabled(true);
    }
  };

  const RightAction = () => (
    <>
      {isDirty ? (
        <IconButton onClick={submitNote} size='small'>
          <Check />
        </IconButton>
      ) : (
        <IconButton onClick={handleDeleteConfirm} size='small'>
          {isNew ? <Close /> : <BinIcon />}
        </IconButton>
      )}
    </>
  );

  return (
    <Note>
      <NoteContent disabled={isDisabled}>
        <NoteHeader data-role='note-header'>
          {isNew ? 'Новая заметка' : dateDisplay}
          <RightAction />
        </NoteHeader>

        <NoteBodyWrapper>
          <NoteBody
            autoFocus={isNew}
            value={value}
            onChange={handleChange}
            multiline
            placeholder='Enter для сохранения, Esc для отмены'
            onKeyDown={handleKeyDown}
          />

          {isDisabled && (
            <OnDeleteOverlay>
              <OverlayContent>
                <CancelDeleteButton onClick={() => setIsDisabled(false)}>
                  Оставить
                </CancelDeleteButton>
                <DeleteNoteButton onClick={handleDelete}>Удалить навсегда</DeleteNoteButton>
              </OverlayContent>
            </OnDeleteOverlay>
          )}
        </NoteBodyWrapper>
      </NoteContent>
    </Note>
  );
};

export default NoteTextField;
